import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const SESSION_KEY = 'betaBannerHidden';

interface BetaBannerProps {
  /** Callback wenn der User auf den Banner-Body klickt — öffnet das BugReportModal. */
  onReport: () => void;
}

/**
 * Phase 15.2.1 — Permanenter gelber Streifen ganz oben. Setzt eine CSS-Variable
 * `--beta-banner-height: 32px` auf dem `<html>`-Element, damit Layouts darunter
 * darauf reagieren können (TopBar wird dadurch nicht hardcodiert verschoben —
 * sie ist sowieso `fixed`, aber andere Komponenten können den Wert lesen).
 *
 * Schließbar via X (sessionStorage, taucht beim Reload wieder auf).
 */
export function BetaBanner({ onReport }: BetaBannerProps) {
  const [hidden, setHidden] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (hidden) {
      document.documentElement.style.setProperty('--beta-banner-height', '0px');
    } else {
      document.documentElement.style.setProperty('--beta-banner-height', '32px');
    }
    return () => {
      document.documentElement.style.setProperty('--beta-banner-height', '0px');
    };
  }, [hidden]);

  if (!__IS_BETA__ || hidden) return null;

  const close = () => {
    setHidden(true);
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // ignore
    }
  };

  return (
    <div
      role="banner"
      className="fixed inset-x-0 top-0 z-50 flex h-8 items-center justify-between bg-amber-400 px-3 text-[12px] font-medium text-amber-950 shadow"
      data-testid="beta-banner"
    >
      <button
        type="button"
        onClick={onReport}
        className="flex flex-1 items-center justify-center gap-1 truncate text-center transition hover:underline"
        aria-label="Bug melden"
      >
        <span aria-hidden>🧪</span>
        <span className="hidden sm:inline">
          Closed Beta v{__APP_VERSION__} · Bug gefunden? Klick hier zum Melden
        </span>
        <span className="inline sm:hidden">Beta · Bug melden</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
        aria-label="Banner schließen"
        className="ml-2 flex h-6 w-6 items-center justify-center rounded transition hover:bg-amber-500/40"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
